import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { useXeroOrgName } from "@/hooks/useXeroOrgName";
import XeroEntityCell from "@/components/admin/XeroEntityCell";
import XeroRetrievalDateCell from "@/components/admin/XeroRetrievalDateCell";
import XeroPre2022SourceCell from "@/components/admin/XeroPre2022SourceCell";
import XeroLegacyOfficeCell from "@/components/admin/XeroLegacyOfficeCell";
import {
  XERO_ORG_RULES_QUERY_KEY,
  XeroOrgRulesOrganisation,
  XeroOrganisationProfilePayload,
  saveXeroOrganisationProfile,
} from "@/services/xeroApi";
import type { Pre2022Source } from "@/services/xeroApi";


function formatUpdatedAt(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-GB");
}

export default function XeroOrganisationRow({
  organisation,
}: {
  organisation: XeroOrgRulesOrganisation;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const orgName = useXeroOrgName();
  const tenantName = orgName(organisation.tenant_id, organisation.tenant_name);

  const [entityDraft, setEntityDraft] = useState(organisation.entity ?? "");
  const [retrievalDateDraft, setRetrievalDateDraft] = useState(
    organisation.retrieval_date,
  );
  const [pre2022Draft, setPre2022Draft] = useState<Pre2022Source | null>(
    organisation.pre2022_source,
  );
  const [legacyOfficeDraft, setLegacyOfficeDraft] = useState(
    organisation.default_legacy_office ?? "",
  );
  const [requiredInCubeDraft, setRequiredInCubeDraft] = useState(
    organisation.required_in_cube,
  );

  const nextEntity = entityDraft.trim() || null;
  const nextLegacyOffice = legacyOfficeDraft.trim() || null;
  const isDirty =
    nextEntity !== organisation.entity ||
    retrievalDateDraft !== organisation.retrieval_date ||
    pre2022Draft !== organisation.pre2022_source ||
    nextLegacyOffice !== organisation.default_legacy_office ||
    requiredInCubeDraft !== organisation.required_in_cube;

  const canSave = organisation.row_id !== null;

  const save = useMutation({
    mutationFn: (payload: XeroOrganisationProfilePayload) =>
      saveXeroOrganisationProfile(organisation.row_id as string, payload),
    onSuccess: () => {
      toast({ title: "Organisation updated" });
      queryClient.invalidateQueries({ queryKey: XERO_ORG_RULES_QUERY_KEY });
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: `Couldn’t update ${tenantName}`,
        description: (e as Error).message,
      });
    },
  });

  const onSave = () => {
    if (!isDirty || save.isPending || !canSave) return;
    save.mutate({
      entity: nextEntity,
      retrieval_date: retrievalDateDraft,
      pre2022_source: pre2022Draft,
      default_legacy_office: nextLegacyOffice,
      required_in_cube: requiredInCubeDraft,
    });
  };

  const onCancel = () => {
    setEntityDraft(organisation.entity ?? "");
    setRetrievalDateDraft(organisation.retrieval_date);
    setPre2022Draft(organisation.pre2022_source);
    setLegacyOfficeDraft(organisation.default_legacy_office ?? "");
    setRequiredInCubeDraft(organisation.required_in_cube);
  };

  return (
    <TableRow>
      <TableCell className="font-medium text-foreground">
        <span className="flex items-center gap-2">
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
          {organisation.tenant_name || "—"}
        </span>
      </TableCell>
      <TableCell>
        <XeroEntityCell
          draft={entityDraft}
          tenantName={tenantName}
          isPending={save.isPending}
          onDraftChange={setEntityDraft}
        />
      </TableCell>
      <TableCell>
        <XeroRetrievalDateCell
          draft={retrievalDateDraft}
          tenantName={tenantName}
          isPending={save.isPending}
          onDraftChange={setRetrievalDateDraft}
        />
      </TableCell>
      <TableCell>
        <XeroPre2022SourceCell
          draft={pre2022Draft}
          tenantName={tenantName}
          isPending={save.isPending}
          onDraftChange={setPre2022Draft}
        />
      </TableCell>
      <TableCell>
        <XeroLegacyOfficeCell
          draft={legacyOfficeDraft}
          tenantName={tenantName}
          isPending={save.isPending}
          onDraftChange={setLegacyOfficeDraft}
        />
      </TableCell>
      <TableCell>
        <Checkbox
          checked={requiredInCubeDraft}
          disabled={save.isPending}
          aria-label={`Required in cube for ${tenantName}`}
          onCheckedChange={(checked) => setRequiredInCubeDraft(checked === true)}
        />
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {organisation.taken_from || "—"}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {formatUpdatedAt(organisation.updated_at)}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="h-8"
            onClick={onSave}
            disabled={!isDirty || save.isPending || !canSave}
            title={canSave ? undefined : "This row has no row_id yet and cannot be saved."}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            onClick={onCancel}
            disabled={!isDirty || save.isPending}
          >
            Cancel
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
